// Small same-origin HTTP client built on the browser Fetch API.
//
// It deliberately preserves the tiny Axios-shaped surface used by CAM
// (`api.get/post/put/patch/delete`, `response.data`, `error.response`) so the
// rest of the frontend remains unchanged while avoiding a runtime HTTP-client
// dependency.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api/v1` : "/api/v1";

function readCookie(name) {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function resolveUrl(path, params) {
  const raw = /^https?:\/\//i.test(path)
    ? path
    : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

  if (!params || Object.keys(params).length === 0) return raw;

  const url = new URL(raw, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
    } else {
      url.searchParams.set(key, String(value));
    }
  });

  return /^https?:\/\//i.test(raw) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

function isBodyLike(value) {
  return (
    typeof value === "string" ||
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value)
  );
}

async function parseResponse(response, responseType) {
  if (response.status === 204 || response.status === 205) return null;
  if (responseType === "blob") return response.blob();
  if (responseType === "arraybuffer") return response.arrayBuffer();
  if (responseType === "text") return response.text();

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const text = await response.text();
  return text || null;
}

function httpError(response, data, config) {
  const detail =
    typeof data?.detail === "string"
      ? data.detail
      : `Request failed with status code ${response.status}`;
  const error = new Error(detail);
  error.name = "HttpError";
  error.config = config;
  error.response = {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config,
  };
  return error;
}

async function rawRequest(method, path, data, config = {}, { skipCsrf = false } = {}) {
  const upperMethod = method.toUpperCase();
  const headers = new Headers(config.headers || {});
  const stateChanging = ["POST", "PUT", "PATCH", "DELETE"].includes(upperMethod);

  if (stateChanging && !skipCsrf && !headers.has("X-CSRF-Token")) {
    const csrf = readCookie("csrf_token");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  let body;
  if (data !== undefined && data !== null) {
    if (isBodyLike(data)) {
      body = data;
    } else {
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      body = JSON.stringify(data);
    }
  }

  const requestConfig = {
    method: upperMethod,
    url: path,
    data,
    ...config,
    headers,
  };

  const response = await fetch(resolveUrl(path, config.params), {
    method: upperMethod,
    headers,
    body,
    credentials: config.credentials || "include",
    signal: config.signal,
    cache: config.cache,
  });

  const responseData = await parseResponse(response, config.responseType);
  if (!response.ok) throw httpError(response, responseData, requestConfig);

  return {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config: requestConfig,
  };
}

let refreshInFlight = null;

async function request(method, path, data, config = {}, allowRefresh = true) {
  try {
    return await rawRequest(method, path, data, config);
  } catch (error) {
    const status = error.response?.status;
    const authRequest = String(path).includes("/auth/");

    if (status !== 401 || !allowRefresh || authRequest) throw error;

    if (!refreshInFlight) {
      // The previous Axios implementation deliberately used the bare client
      // for refresh, so no CSRF interceptor ran on this request. Preserve that
      // behavior; the refreshed access/CSRF cookies are then used by the retry.
      refreshInFlight = rawRequest(
        "POST",
        "/auth/refresh",
        null,
        {},
        { skipCsrf: true },
      ).finally(() => {
        refreshInFlight = null;
      });
    }

    try {
      await refreshInFlight;
    } catch {
      throw error;
    }

    return request(method, path, data, config, false);
  }
}

export const api = {
  get(path, config) {
    return request("GET", path, undefined, config);
  },
  post(path, data, config) {
    return request("POST", path, data, config);
  },
  put(path, data, config) {
    return request("PUT", path, data, config);
  },
  patch(path, data, config) {
    return request("PATCH", path, data, config);
  },
  delete(path, config) {
    return request("DELETE", path, undefined, config);
  },
};

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d?.msg === "string" ? d.msg : JSON.stringify(d))).join(" — ");
  }
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return "Une erreur est survenue.";
}

export function wsUrl(path) {
  if (!BACKEND_URL) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}/api/v1${path}`;
  }
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/v1${path}`;
}
